// Totais de um conjunto de SERVIÇOS.
//
// A árvore de totais (`budgetValuesTotal.byCalculationType[forma][categoria][]`)
// agrega por `supplierId` e, com isso, perde a dimensão do agrupamento: não há
// como ler dela o total do "Dia 1". O total por agrupamento precisa ser
// re-acumulado.
//
// Re-acumular NÃO é um segundo caminho de cálculo. `calculateDetailedTotals`
// (calc-methods.ts) já é uma acumulação por serviço; o que está aqui é a MESMA
// fórmula, com o mesmo corte de `zeroTotalTax`, apenas particionada por outro
// critério. O teste que trava isso compara as duas: somar todos os agrupamentos
// de um par (fornecedor, categoria) tem de dar exatamente o valor da árvore.
//
// Se a fórmula da árvore mudar, esta precisa mudar junto — é o preço de o
// agrupamento não caber no formato agregado que já existe.

import { zeroTotalTax } from "../contexts/BudgetContext/calc-methods";
import { ServiceData, ServiceValues } from "../types/budget/service-item";
import {
  CategoryTotals,
  emptyTotalSplited,
  versionInfo,
} from "../types/budget/budget";
import { resolveServiceCalcOption } from "./budget-phase-calc-option";

export type GroupTotals = {
  tariffTotals: number;
  serviceTaxTotals: number;
  revenueTotals: number;
  taxes: number;
  totals: number;
};

export const emptyGroupTotals = (): GroupTotals => ({
  tariffTotals: 0,
  serviceTaxTotals: 0,
  revenueTotals: 0,
  taxes: 0,
  totals: 0,
});

/**
 * Bloco de valores que a etapa exibida lê de cada serviço.
 *
 * Mesmo desempate de `resolveStageTotalsSource`, mas no serviço em vez da
 * árvore: Fechamento lê `closingValues` (caindo em `budgetValues` quando ainda
 * não existe), Negociação lê `negotiationValue[versão - 1]`, e o resto lê
 * `budgetValues`.
 */
export function resolveServiceValuesForStage(
  service: ServiceData,
  stage: Pick<versionInfo, "status" | "version"> | undefined
): ServiceValues | undefined {
  const version = stage?.version ?? 0;

  if (stage?.status === "APPROVED") {
    return service.closingValues ?? service.budgetValues;
  }

  if (stage?.status === "NEGOTIATE" && version > 0) {
    return service.negotiationValue?.[version - 1] ?? service.budgetValues;
  }

  return service.budgetValues;
}

/**
 * Soma os cinco subtotais exibidos, para os serviços recebidos.
 *
 * Espelha `calculateDetailedTotals`:
 *
 *   totals           = Σ values.totals.totalInBRL
 *   tariffTotals     = Σ taxData.tariff.valueBRL
 *   serviceTaxTotals = Σ taxData.serviceTax.valueBRL
 *   revenueTotals    = Σ (fee + markup + overhead + commission + incentive).valueBRL
 *   taxes            = Σ (iss + cbs + ibs + tribute + localTax).valueBRL
 *
 * Serviço com tarifa em `zeroTotalTax` (cortesia, a cargo do cliente, incluído)
 * é ignorado — igual à árvore.
 */
export function sumServiceTotals(
  services: ServiceData[],
  stage: Pick<versionInfo, "status" | "version"> | undefined
): GroupTotals {
  const totals = emptyGroupTotals();

  for (const service of services) {
    const values = resolveServiceValuesForStage(service, stage);
    const taxData = values?.taxData;
    if (!taxData) continue;
    if (zeroTotalTax.includes(taxData.tariff?.typeTax ?? "")) continue;

    const brl = (item: { valueBRL?: number } | undefined) =>
      item?.valueBRL ?? 0;

    totals.totals += values?.totals?.totalInBRL ?? 0;
    totals.tariffTotals += brl(taxData.tariff);
    totals.serviceTaxTotals += brl(taxData.serviceTax);
    totals.revenueTotals +=
      brl(taxData.fee) +
      brl(taxData.markup) +
      brl(taxData.overhead) +
      brl(taxData.commission) +
      brl(taxData.incentive);
    totals.taxes +=
      brl(taxData.iss) +
      brl(taxData.cbs) +
      brl(taxData.ibs) +
      brl(taxData.tribute) +
      brl(taxData.localTax);
  }

  return totals;
}

/**
 * Adapta os cinco subtotais ao formato que o cabeçalho da categoria consome.
 *
 * Só os cinco são preenchidos — são exatamente os que o cabeçalho exibe. As
 * demais chaves ficam zeradas de propósito: o detalhamento por imposto vive no
 * painel "Valores", que é da categoria inteira e não do agrupamento.
 *
 * `totalInCurrency` fica zerado pelo mesmo motivo do BRL ser a única moeda
 * somável: cada serviço pode ter câmbio próprio, e o cabeçalho já mostra os
 * valores em BRL.
 */
export function toCategoryTotals(totals: GroupTotals): CategoryTotals {
  const brl = (value: number) => ({ totalInBRL: value, totalInCurrency: 0 });

  return {
    ...emptyTotalSplited,
    tariffTotals: brl(totals.tariffTotals),
    serviceTaxTotals: brl(totals.serviceTaxTotals),
    revenueTotals: brl(totals.revenueTotals),
    taxes: brl(totals.taxes),
    totals: brl(totals.totals),
  };
}

/**
 * Serviços que pertencem a uma LINHA (categoria + fornecedor) dentro de um
 * agrupamento, na etapa exibida.
 *
 * `groupId` ausente no serviço casa com `groupId` ausente na linha: é o par
 * "Sem agrupamento", que é o estado de todo orçamento legado.
 */
export function servicesOfRow(
  budgetServices: ServiceData[] | undefined,
  row: {
    type: string;
    supplierId: string;
    calcOption: string;
    groupId?: string;
  },
  version: number,
  status?: string
): ServiceData[] {
  const stage = { version, status };
  return (budgetServices ?? []).filter(
    (service) =>
      service.type === row.type &&
      service.supplierId === row.supplierId &&
      resolveServiceCalcOption(service, stage) === row.calcOption &&
      (service.groupId ?? undefined) === (row.groupId ?? undefined) &&
      (service.versions ?? [0]).includes(version)
  );
}
